import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiInfo, FiCheck, FiExternalLink, FiDatabase, FiSearch, FiFilter } = FiIcons

const AboutSection = () => {
  const { currentLang, translations } = useApp()

  const features = [
    translations.feature1[currentLang],
    translations.feature2[currentLang],
    translations.feature3[currentLang],
    translations.feature4[currentLang]
  ]

  // 內部連結資料
  const internalLinks = [
    {
      href: "#search-section",
      icon: FiSearch,
      text: currentLang === 'zh' ? '搜尋銀行' : 'Search Banks'
    },
    {
      href: "#results-section",
      icon: FiDatabase,
      text: currentLang === 'zh' ? '瀏覽結果' : 'Browse Results'
    },
    {
      href: "#quick-navigation",
      icon: FiFilter,
      text: currentLang === 'zh' ? '快速篩選' : 'Quick Filter'
    }
  ]

  return (
    <motion.section
      id="about-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.6 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <motion.div 
          className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md"
          whileHover={{ 
            y: -2,
            rotate: 10,
            boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.3)",
            transition: { duration: 0.2 }
          }}
          transition={{ duration: 0.2 }}
        >
          <SafeIcon icon={FiInfo} className="w-4 h-4 text-white" />
        </motion.div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          {translations.aboutTitle[currentLang]}
        </h2>
      </div>

      {/* 主要功能區域 - 增強視覺效果 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" />
          {translations.aboutFeatures[currentLang]}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
              whileHover={{ 
                x: 5,
                boxShadow: "0 4px 12px -2px rgba(34, 197, 94, 0.25)",
                borderColor: "rgba(34, 197, 94, 0.4)",
                transition: { duration: 0.2 }
              }}
              className="flex items-start gap-3 p-3 rounded-lg bg-green-50/50 dark:bg-green-900/10 border border-green-100 dark:border-green-800/30 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200"
            >
              <div className="w-5 h-5 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
              </div>
              <span className="text-gray-700 dark:text-gray-300 text-sm sm:text-base leading-relaxed">{feature}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 內部導航連結區域 - 提升SEO */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {currentLang === 'zh' ? '快速導航' : 'Quick Navigation'}
        </h3>
        <div className="flex flex-wrap gap-3">
          {internalLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.href}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 1.0 + index * 0.1 }}
              whileHover={{ 
                y: -2,
                boxShadow: "0 4px 12px -2px rgba(59, 130, 246, 0.25)",
                borderColor: "rgba(59, 130, 246, 0.4)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 text-sm font-medium"
              onClick={(e) => {
                e.preventDefault()
                const target = document.querySelector(link.href)
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
            >
              <SafeIcon icon={link.icon} className="w-4 h-4" />
              {link.text}
              <SafeIcon icon={FiExternalLink} className="w-3 h-3 opacity-60" />
            </motion.a>
          ))}
        </div>
      </div>

      {/* 描述和資料來源區域 */}
      <div className="space-y-6">
        <motion.p 
          className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm sm:text-base"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.4 }}
        >
          {translations.aboutDescription[currentLang]}
        </motion.p>
        
        <motion.div 
          className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-gradient-to-r from-gray-50 to-blue-50/30 dark:from-gray-700/50 dark:to-blue-900/10 rounded-lg border border-gray-200 dark:border-gray-600"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.6 }}
        >
          <div className="flex items-start gap-2">
            <SafeIcon icon={FiDatabase} className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                {currentLang === 'zh' ? '資料來源' : 'Data Source'}
              </div>
              {translations.dataSource[currentLang]}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  )
}

export default AboutSection